import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faCalendar } from "@fortawesome/pro-solid-svg-icons/faCalendar";
import { faCopy } from "@fortawesome/pro-solid-svg-icons/faCopy";
import { faPaperPlane } from "@fortawesome/pro-solid-svg-icons/faPaperPlane";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import Popconfirm from "antd/es/popconfirm";
import { useIntl } from "react-intl";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { LoadingSpinner } from "@mapmycustomers/ui";

import MoreButton from "@app/component/MoreButton";
import { fetchReportRecordsCountInfo } from "@app/scene/reports/store/actions";
import { isReportRecordsCountLoading } from "@app/scene/reports/store/selectors";
import { RootState } from "@app/store/rootReducer";
import Report from "@app/types/Report";
import { EXPORT_RECORD_COUNT_LIMIT } from "@app/util/consts";

import messages from "./messages";
import styles from "./ReportListActions.module.scss";

interface Props {
  onClone?: (report: Report) => void;
  onDelete?: (report: Report) => void;
  onDownload?: (report: Report) => void;
  onFetchReportRecordsCountInfo: typeof fetchReportRecordsCountInfo.request;
  onSchedule?: (report: Report) => void;
  recordsCountLoading: boolean;
  report: Report;
}

const ReportListActions: React.FC<Props> = ({
  onClone,
  onDelete,
  onDownload,
  onFetchReportRecordsCountInfo,
  onSchedule,
  recordsCountLoading,
  report,
}) => {
  const intl = useIntl();

  const [menuVisible, , hideMenu, toggleMenu] = useBoolean();
  const [downloadConfirmationVisible, showDownloadConfirmation, hideDownloadConfirmation] =
    useBoolean();

  const handleVisibilityChange = useCallback(
    (visibility: boolean) => {
      if (!visibility) {
        hideMenu();
      }
    },
    [hideMenu]
  );

  const handleDownload = useCallback(() => {
    onDownload?.(report);
    hideDownloadConfirmation();
    hideMenu();
  }, [hideDownloadConfirmation, hideMenu, onDownload, report]);

  const handleSchedule = useCallback(() => {
    onSchedule?.(report);
    hideMenu();
  }, [hideMenu, onSchedule, report]);

  const handleClone = useCallback(() => {
    onClone?.(report);
    hideMenu();
  }, [hideMenu, onClone, report]);

  const handleFetchReportRecordsCount = useCallback(() => {
    onFetchReportRecordsCountInfo({
      callback: (count) => {
        (count > EXPORT_RECORD_COUNT_LIMIT ? showDownloadConfirmation : handleDownload)();
      },
      report,
    });
  }, [onFetchReportRecordsCountInfo, handleDownload, report, showDownloadConfirmation]);

  const handleDeleteConfirm = useCallback(() => {
    onDelete?.(report);
    hideMenu();
  }, [hideMenu, onDelete, report]);

  const menuItems = useMemo(() => {
    return [
      ...(onDownload
        ? [
            {
              icon: <FontAwesomeIcon icon={faPaperPlane} />,
              key: "quickSend",
              label: (
                <Popconfirm
                  cancelText={intl.formatMessage(messages.downloadConfirmationNo)}
                  okText={intl.formatMessage(messages.downloadConfirmationYes)}
                  onCancel={hideDownloadConfirmation}
                  onConfirm={handleDownload}
                  open={downloadConfirmationVisible}
                  overlayClassName={styles.popconfirm}
                  title={intl.formatMessage(messages.downloadConfirmation, {
                    br: () => <br />,
                    limit: intl.formatNumber(EXPORT_RECORD_COUNT_LIMIT),
                  })}
                >
                  <span className={styles.download} onClick={handleFetchReportRecordsCount}>
                    {intl.formatMessage(messages.quickSend)}
                    <span className={styles.loadSpinnerContainer}>
                      {recordsCountLoading && <LoadingSpinner mini />}
                    </span>
                  </span>
                </Popconfirm>
              ),
            },
          ]
        : []),
      ...(onSchedule
        ? [
            {
              icon: <FontAwesomeIcon icon={faCalendar} />,
              key: "schedule",
              label: report.frequency
                ? intl.formatMessage(messages.scheduleSendFrequency)
                : intl.formatMessage(messages.scheduleSend),
              onClick: handleSchedule,
            },
          ]
        : []),
      ...(onClone
        ? [
            {
              icon: <FontAwesomeIcon icon={faCopy} />,
              key: "createCopy",
              label: intl.formatMessage(messages.createCopy),
              onClick: handleClone,
            },
          ]
        : []),
      ...(onDelete
        ? [
            {
              danger: true,
              icon: <FontAwesomeIcon icon={faTrash} />,
              key: "delete",
              label: (
                <Popconfirm
                  cancelText={intl.formatMessage(messages.deleteExportConfirmNo)}
                  okText={intl.formatMessage(messages.deleteExportConfirmYes)}
                  onConfirm={handleDeleteConfirm}
                  overlayClassName={styles.popconfirm}
                  title={intl.formatMessage(messages.deleteExportConfirm)}
                >
                  {intl.formatMessage(messages.deleteExport)}
                </Popconfirm>
              ),
            },
          ]
        : []),
    ];
  }, [
    downloadConfirmationVisible,
    handleClone,
    handleDeleteConfirm,
    handleDownload,
    handleFetchReportRecordsCount,
    handleSchedule,
    hideDownloadConfirmation,
    intl,
    onClone,
    onDelete,
    onDownload,
    onSchedule,
    recordsCountLoading,
    report.frequency,
  ]);

  if (!menuItems.length) {
    return null;
  }

  return (
    <Dropdown
      key="menu"
      menu={{ items: menuItems }}
      onOpenChange={handleVisibilityChange}
      open={menuVisible}
      placement="bottomRight"
      trigger={["click"]}
    >
      <MoreButton horizontal onClick={toggleMenu} />
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  recordsCountLoading: isReportRecordsCountLoading(state),
});

const mapDispatchToProps = {
  onFetchReportRecordsCountInfo: fetchReportRecordsCountInfo.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReportListActions);
